import React from "react";
import styled from "styled-components";
import Xarrow from "react-xarrows";
import {
    ActionButton,
    ActionsRender,
    StyledCircle,
    XMappingDragItem,
    XMappingItemRender,
    XMappingListRender,
    XMappingSectionHeader,
} from "../Builder/CommonStyles";
import { CollapseIcon } from "../../CommonUI/Icon/CollapseIcon";
import { MappingEditContext } from "../Builder/MappingEditContext";
import { ExpandGrayIcon } from "../../CommonUI/Icon/ExpandGrayIcon";
import { MappingConstants } from "../../rule-engine-core/Mapping/MappingConstants";
import { MappingConfiguration, MappingRule } from "../../rule-engine-core/Mapping/MappingConfiguration";
import { useXarrow } from "react-xarrows";
import { XMappingRule } from "./XMappingRule";
import { ObserveMultiState } from "../../CommonUI/StateManagement/ObserveMultiState";
import { DragConnector } from "./DragConnector";
import { DataUtilities } from "../../rule-engine-core/Common/DataUtilities";
import { IDataDefinition } from "../../rule-engine-core/Common/Data/Definitions/IDataDefinition";
import { ObjectDataDefinition } from "../../rule-engine-core/Common/Data/Definitions/ObjectDataDefinition";
import { useDrop } from "react-dnd";
import { StateManager } from "../../rule-engine-core/Common/StateManager";
import { UIStyleConstants } from "../../rule-engine-core/Common/UIStyleConstants";

export interface IMappingDestination {
    editContext: MappingEditContext;
    definition: MappingConfiguration;
}

export const XMappingDestination = (props: IMappingDestination) => {
    const updateXarrow = useXarrow();

    const ref = React.useRef<HTMLDivElement | null>(null);

    return (
        <XMappingDestinationRender>
            <XMappingDestinationHeader>
                <XMappingSectionHeader className="title">Destination</XMappingSectionHeader>
            </XMappingDestinationHeader>

            <XMappingDestinationVerticalLine />
            <XMappingListRender
                ref={ref}
                onScroll={(e) => {
                    if (ref.current) {
                        ref.current.scrollLeft = 0;
                    }

                    if (e.target === ref.current) {
                        updateXarrow();
                    }
                }}
            >
                <ObserveMultiState
                    listenTo={[props.definition, props.editContext]}
                    control={() => {
                        const invalidRules = props.definition.getInvalidRules();

                        return (
                            <XDestinationList>
                                {invalidRules.length > 0 && (
                                    <XMappingMissingRows>
                                        {invalidRules.map((mappingRule, idx) => (
                                            <XMappingDestinationListItem key={idx}>
                                                <XMappingRuleSection>
                                                    <XMappingRule
                                                        mappingRule={mappingRule}
                                                        editContext={props.editContext}
                                                        definition={props.definition}
                                                        isInvalid
                                                    />
                                                </XMappingRuleSection>

                                                <XMappingDestinationSection
                                                    size={"Medium"}
                                                    deepsCount={0}
                                                >
                                                    <XMappingDestinationItem
                                                        path={mappingRule.targetPath}
                                                        editContext={props.editContext}
                                                        definition={props.definition}
                                                        isInvalid
                                                        mappingRule={mappingRule}
                                                    />
                                                </XMappingDestinationSection>
                                            </XMappingDestinationListItem>
                                        ))}
                                    </XMappingMissingRows>
                                )}

                                {DataUtilities.getAllSubFieldsWithPath(props.definition.getExecutionResult()).map(
                                    (fieldWithPath, idx) => {
                                        const { field, path } = fieldWithPath;
                                        let mappingRule = props.definition.getMappingRuleByTargetPath(path);

                                        let isCollapsed = props.editContext.isDestinationCollapsed(path);

                                        return (
                                            <XMappingDestinationListItem key={idx}>
                                                <XMappingRuleSection>
                                                    <XMappingRule
                                                        mappingRule={mappingRule}
                                                        editContext={props.editContext}
                                                        definition={props.definition}
                                                    />
                                                </XMappingRuleSection>

                                                <XMappingDestinationSection
                                                    size={path.includes(".") ? "Small" : "Medium"}
                                                    deepsCount={path.split(".").length - 1}
                                                    isCollapsed={isCollapsed}
                                                >
                                                    {isCollapsed ? (
                                                        <div style={{ position: "relative", marginTop: -4 }}>
                                                            <XMappingConnectorForMappingRule
                                                                id={`${MappingConstants.MAPPING_PREFIX_RULE_ITEM}${path}_connector_formappingrule`}
                                                            />
                                                        </div>
                                                    ) : (
                                                        <XMappingDestinationItem
                                                            path={mappingRule.targetPath}
                                                            editContext={props.editContext}
                                                            definition={props.definition}
                                                            field={field}
                                                            mappingRule={mappingRule}
                                                        />
                                                    )}
                                                </XMappingDestinationSection>
                                            </XMappingDestinationListItem>
                                        );
                                    },
                                )}
                            </XDestinationList>
                        );
                    }}
                />
            </XMappingListRender>
        </XMappingDestinationRender>
    );
};

export interface IMappingDestinationItem {
    field?: IDataDefinition;
    editContext: MappingEditContext;
    path: string;
    definition: MappingConfiguration;
    isInvalid?: boolean;
    mappingRule: MappingRule;
}

export const XMappingDestinationItem = (props: IMappingDestinationItem) => {
    function onDragEndField(sourceField: string): void {
        props.editContext.isDragging = false;

        props.definition.hookDirectConnect(sourceField, props.path);

        StateManager.changed(props.definition);

        props.editContext.delayPosition();
    }

    function onDragEndMapping(mappingRule: MappingRule): void {
        props.editContext.isDragging = false;

        let oldRule = props.definition.getMappingRuleByTargetPath(props.path);
        // remove old rule if it exists
        if (oldRule) {
            props.definition.removeMappingRule(oldRule.id);
        }

        mappingRule.targetPath = props.path;

        let field = DataUtilities.getFieldFromDataDefinitionByName(
            props.definition.getExecutionResult().fields,
            props.path,
        ) as IDataDefinition;

        mappingRule.mapping.executionResult = () => field!;

        StateManager.changed(props.definition);

        props.editContext.delayPosition();
    }

    const [{ canDrop, isOver }, drop] = useDrop({
        accept: "field",
        drop: (i: any) => {
            if (Reflect.get(i, "path")) {
                onDragEndField(i.path);
            } else if (Reflect.get(i, "rule")) {
                onDragEndMapping(i.rule);
            }
        },
        collect: (monitor) => ({
            isOver: monitor.isOver(),
            canDrop: monitor.canDrop(),
        }),
    });

    let parentPath: string | undefined;

    if (props.path.includes(".")) {
        let pieces = props.path.split(".");
        parentPath = pieces.slice(0, -1).join(".");
    }

    let isCurrentCollapsed = props.editContext.isDestinationCollapsed(props.path, false);

    let forDestinationId = props.editContext.ruleIdForDestination(props.mappingRule.id);

    let currentIdForDroppable = props.editContext.destinationId(props.path);
    //    let currentIdForDraggable = props.editContext.ruleId(props.mappingRule.id);

    let itemSize: "Small" | "Medium" = props.path.includes(".") ? "Small" : "Medium";

    return (
        <XMappingItemRender
            id={currentIdForDroppable}
            isDraggingOver={canDrop && isOver}
            size={props.path && props.path.includes(".") ? "Small" : "Medium"}
            ref={drop}
            isInvalid={props.isInvalid}
        >
            {props.isInvalid ? (
                <XMappingDragItem size={"Medium"}>{`Missing: ${props.path}`}</XMappingDragItem>
            ) : (
                <>
                    <XMappingDragItem
                        size={itemSize}
                        isDragging={isOver}
                        highlighted={isOver}
                        onMouseEnter={() => {
                            if (!props.mappingRule.empty) {
                                props.editContext.hightlightedRules = [props.mappingRule.id];
                            }
                        }}
                        onMouseLeave={() => {
                            if (!props.mappingRule.empty) {
                                props.editContext.hightlightedRules = [];
                            }
                        }}
                    >
                        {props.path}
                    </XMappingDragItem>
                </>
            )}

            {!props.mappingRule.empty && (
                <DragConnector
                    fromId={forDestinationId}
                    toId={props.editContext.destinationDragId(props.mappingRule.id)}
                    direction="left"
                    icon={<StyledCircle />}
                    lineColor={
                        props.editContext.isHighlighted(props.mappingRule.id)
                            ? MappingConstants.HIGHLIGHT_MAPPING_LINE_COLOR
                            : MappingConstants.MAPPING_LINE_COLOR
                    }
                    lineWidth={props.editContext.isHighlighted(props.mappingRule.id) ? 2 : 1}
                    mappingRule={props.mappingRule}
                />
            )}

            {props.field instanceof ObjectDataDefinition ? (
                <>
                    <XMappingConnectorForChildren
                        id={props.editContext.destinationConnectorIdForChildren(props.path)}
                    />
                    <ActionsRender className="ActionsRender">
                        <ActionButton
                            onClick={(event: React.MouseEvent<HTMLDivElement>) => {
                                event.stopPropagation();
                                props.editContext.toggleDestinationCollapse(props.path);
                            }}
                        >
                            {!isCurrentCollapsed ? <CollapseIcon /> : <ExpandGrayIcon />}
                        </ActionButton>
                    </ActionsRender>
                </>
            ) : parentPath ? (
                <>
                    <XMappingConnectorForParent id={props.editContext.destinationConnectorIdForParent(props.path)} />
                    <Xarrow
                        start={props.editContext.destinationConnectorIdForParent(props.path)}
                        color={MappingConstants.MAPPING_LINE_COLOR}
                        end={props.editContext.destinationConnectorIdForChildren(parentPath)}
                        startAnchor="left"
                        endAnchor="bottom"
                        strokeWidth={10}
                        showHead={false}
                        path="grid"
                    />
                </>
            ) : null}
        </XMappingItemRender>
    );
};

const XMappingDestinationRender = styled.div`
    width: 400px;
    display: flex;
    flex-direction: column;
    height: 100%;
    position: relative;

    padding-bottom: 20px;
`;

const XMappingConnectorForMappingRule = styled.div`
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    left: 0;
`;

const XMappingConnectorForChildren = styled.div`
    position: absolute;
    top: 100%;
    right: 8px;
`;

const XMappingConnectorForParent = styled.div`
    position: absolute;
    top: 50%;
    right: 0px;
`;

const XDestinationList = styled.div`
    display: flex;
    flex-direction: column;

    margin-right: 30px;
`;

const XMappingMissingRows = styled.div`
    background: ${UIStyleConstants.GRAY_LIGHT_COLOR};
`;

const XMappingRuleSection = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;

    width: 100px;
`;

const XMappingDestinationSection = styled.div<{
    size: "Small" | "Medium";
    deepsCount: number;
    isCollapsed?: boolean;
}>`
    display: flex;
    align-items: center;

    flex: 1;

    padding: ${(props) =>
        props.isCollapsed
            ? `0 ${(props.deepsCount - 1) * 25 + 56}px 0 ${(props.deepsCount - 1) * 8 + 24}px`
            : `${props.size === "Small" ? "2px" : "4px"} ${props.deepsCount * 25 + 56}px ${
                  props.size === "Small" ? "2px" : "4px"
              } ${props.deepsCount * 8 + 24}px`};
`;

const XMappingDestinationVerticalLine = styled.div`
    position: absolute;
    width: 1px;
    height: 100%;
    border-left: 1px dashed ${UIStyleConstants.BLUE_LIGHT_COLOR};
    top: 0;
    left: 100px;
`;

const XMappingDestinationHeader = styled.div`
    display: flex;
    justify-content: flex-end;

    .title {
        width: 300px;
    }
`;

const XMappingDestinationListItem = styled.div`
    display: flex;

    flex-direction: row;

    &:hover {
        .EmptyRule-root {
            opacity: 1;
        }
    }

    position: sticky;
    right: 0;
`;
